// Step navigation logic for onboarding flow
// Pure functions for parsing and building step URLs
// Part of ViewModel layer - provides navigation logic

// ADT for onboarding step.
// Step key 'modality' + UI label 'Modality' map to GraphQL Creator.keywords /
// CreatorProfileUpdateInput.keywords.
export type OnboardingStep =
  | { type: 'modality' }
  | { type: 'revenue-goal' }
  | { type: 'question'; index: number }
  | { type: 'result' }
  | { type: 'strategy' };

const STEP_PARAM = 'step';
const MODALITY_STEP = 'modality';
const REVENUE_GOAL_STEP = 'revenue-goal';
const STRATEGY_STEP = 'strategy';
const RESULT_STEP = 'result';

// Parse step from URL query string. Returns the requested step verbatim;
// eligibility/redirection is enforced in `getValidStep` on the viewmodel.
export const parseStep = (totalQuestions: number): OnboardingStep => {
  const params = new URLSearchParams(window.location.search);
  const stepValue = params.get(STEP_PARAM);

  if (stepValue === MODALITY_STEP) {
    return { type: 'modality' };
  }

  if (stepValue === REVENUE_GOAL_STEP) {
    return { type: 'revenue-goal' };
  }

  if (stepValue === STRATEGY_STEP) {
    return { type: 'strategy' };
  }

  if (stepValue === RESULT_STEP) {
    return { type: 'result' };
  }

  // Question step - parse as number
  const stepNum = stepValue ? parseInt(stepValue, 10) : 1;

  // Validate step is within bounds (1 to totalQuestions)
  if (isNaN(stepNum) || stepNum < 1) {
    return { type: 'question', index: 0 };
  }

  if (stepNum > totalQuestions) {
    return { type: 'strategy' };
  }

  // Convert 1-indexed URL param to 0-indexed
  return { type: 'question', index: stepNum - 1 };
};

// Get base path from current URL (preserves admin context)
const getBasePath = (): string => {
  // Get pathname without query string
  const pathname = window.location.pathname;
  return pathname;
};

// Build URL with step param, preserving current route context
export const buildStepUrl = (step: OnboardingStep): string => {
  let stepValue: string;
  if (step.type === 'modality') {
    stepValue = MODALITY_STEP;
  } else if (step.type === 'revenue-goal') {
    stepValue = REVENUE_GOAL_STEP;
  } else if (step.type === 'strategy') {
    stepValue = STRATEGY_STEP;
  } else if (step.type === 'result') {
    stepValue = RESULT_STEP;
  } else {
    stepValue = String(step.index + 1);
  }
  const basePath = getBasePath();
  return `${basePath}?${STEP_PARAM}=${stepValue}`;
};

// Helper to get current question index from step
export const getQuestionIndex = (step: OnboardingStep): number | null => {
  return step.type === 'question' ? step.index : null;
};

// Helper to check if step is modality picker
export const isModalityStep = (step: OnboardingStep): boolean => {
  return step.type === 'modality';
};

// Helper to check if step is the revenue-goal slider
export const isRevenueGoalStep = (step: OnboardingStep): boolean => {
  return step.type === 'revenue-goal';
};

// Helper to check if step is strategy selection
export const isStrategyStep = (step: OnboardingStep): boolean => {
  return step.type === 'strategy';
};

// Helper to check if step is result screen
export const isResultStep = (step: OnboardingStep): boolean => {
  return step.type === 'result';
};

// Calculate next step
export const getNextStep = (
  currentStep: OnboardingStep,
  totalQuestions: number,
): OnboardingStep | null => {
  // The revenue-goal slider is a fixed step between modality and the questions.
  if (currentStep.type === 'modality') {
    return { type: 'revenue-goal' };
  }

  if (currentStep.type === 'revenue-goal') {
    return totalQuestions > 0
      ? { type: 'question', index: 0 }
      : { type: 'result' };
  }

  if (currentStep.type === 'question') {
    const nextIndex = currentStep.index + 1;
    if (nextIndex >= totalQuestions) {
      return { type: 'result' };
    }
    return { type: 'question', index: nextIndex };
  }

  if (currentStep.type === 'result') {
    return { type: 'strategy' };
  }

  // Already on strategy step
  return null;
};

// Calculate previous step. The modality step is always the first step of the
// flow, before the revenue-goal slider.
export const getPreviousStep = (
  currentStep: OnboardingStep,
  totalQuestions: number,
): OnboardingStep | null => {
  if (currentStep.type === 'strategy') {
    // Go back to result
    return { type: 'result' };
  }

  if (currentStep.type === 'result') {
    // Go back to last question
    return { type: 'question', index: totalQuestions - 1 };
  }

  if (currentStep.type === 'question' && currentStep.index > 0) {
    return { type: 'question', index: currentStep.index - 1 };
  }

  // The revenue-goal slider sits before the first question, so back from
  // question 0 always returns there.
  if (currentStep.type === 'question' && currentStep.index === 0) {
    return { type: 'revenue-goal' };
  }

  // Revenue-goal always goes back to modality, the first step of the flow.
  if (currentStep.type === 'revenue-goal') {
    return { type: 'modality' };
  }

  // Modality has no back.
  return null;
};

// 1-indexed display number for the progress indicator. Step order is
// modality → revenue-goal → questions; both modality and the revenue-goal
// slider are always part of the flow. Result and strategy steps have no
// progress number (returns null).
export const getStepDisplayNumber = (step: OnboardingStep): number | null => {
  if (step.type === 'modality') {
    return 1;
  }
  if (step.type === 'revenue-goal') {
    return 2;
  }
  if (step.type === 'question') {
    // +2 for the modality and revenue-goal steps that precede every question.
    return step.index + 3;
  }
  return null;
};

// Total denominator for the progress indicator: every numbered step in the
// flow. Always includes the modality step and the revenue-goal step.
export const getTotalDisplaySteps = (totalQuestions: number): number => {
  return totalQuestions + 2;
};
